import React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import {
  TableHead,
  Typography,
  useTheme,
  IconButton,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AccountTypeEnum } from "@bn/api/apiv1";
import PlusIcon from "@icons/myicons/plus.svg?react";
import LockIcon from "@icons/myicons/lock.svg?react";
import { ExtendedAccount } from "@features/depots/types";
import { PortfolioLimitGate } from "@features/add/PortfolioLimitGate";
import { SortableTableHeader, TableRow, Table } from "../Table";
import { useAccountsSortAndFilter } from "./useAccountsSortAndFilter";
import { AccountRowLoading } from "./AccountRow";
import { AccountView } from "./AccountView";
import { accountRowId, getRedirectType } from "./utils";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const FullWidthSortableTableHeader = styled(SortableTableHeader)`
  width: 100%;
`;

const NoBorderTableRow = styled(TableRow)`
  height: 61px;
  & > th {
    border-bottom: none;
  }
`;

const NoDataContainer = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.palette.border.secondary};
  text-align: center;
`;

interface Props {
  type: AccountTypeEnum;
  title: string;
  accounts?: ExtendedAccount[];
  refAccountsByDepotId?: Partial<Record<number, ExtendedAccount>>;
  isFetching: boolean;
  filter?: string;
  isLastGroup: boolean;
  onAdd: () => void;
}

export const AccountsContainer = ({
  type,
  title,
  isFetching,
  accounts = [],
  refAccountsByDepotId = {},
  filter = "",
  onAdd,
  isLastGroup,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    sortedAccounts,
    sortField,
    sortDirection,
    sort,
    setPerformanceValues,
  } = useAccountsSortAndFilter(accounts, filter);

  const handleClick = (sortByName: string) => {
    sort(sortByName);
  };

  return (
    <Container
      data-testid={`accounts-list-${type}`}
      style={{ marginBottom: 6 }}
    >
      {isFetching && (
        <>
          <AccountRowLoading />
          <AccountRowLoading />
          <AccountRowLoading />
        </>
      )}
      {!isFetching && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mb={0}
            gap={3}
          >
            <Typography variant="h6">{title}</Typography>
            <PortfolioLimitGate
              renderLocked={
                <IconButton
                  sx={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "6px",
                    backgroundColor:
                      theme.palette.button.tertiaryButton.default,
                    cursor: "not-allowed",
                  }}
                >
                  <LockIcon width="16px" height="16px" />
                </IconButton>
              }
            >
              <IconButton
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: theme.palette.button.tertiaryButton.default,
                }}
                onClick={onAdd}
              >
                <PlusIcon />
              </IconButton>
            </PortfolioLimitGate>
          </Box>
          <Table>
            <TableHead>
              <NoBorderTableRow>
                <FullWidthSortableTableHeader
                  isSortable
                  text={t("accounts.sort.name")}
                  sortByName="name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onClick={handleClick}
                  id={`name-sortable-header-${type}`}
                  data-testid={`name-sortable-header-${type}`}
                  sx={{ color: theme.palette.text.primary }}
                />
                <SortableTableHeader
                  isSortable
                  text={t("accounts.sort.totalValue")}
                  sortByName="performance"
                  align="right"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onClick={handleClick}
                  data-testid={`performance-sortable-header-${type}`}
                  sx={{
                    color: theme.palette.text.primary,
                    minWidth: "200px",
                  }}
                />
              </NoBorderTableRow>
            </TableHead>
          </Table>
          {sortedAccounts.length > 0 ? (
            sortedAccounts.map((account, index) => {
              const referenceAccount = refAccountsByDepotId[account.id];
              const isLastAccountInGroup = index === sortedAccounts.length - 1;
              const redirectType = getRedirectType(account);
              return (
                <React.Fragment key={`account-view-${account.id}`}>
                  <AccountView
                    isLast={
                      !referenceAccount
                        ? isLastAccountInGroup && !isLastGroup
                        : false
                    }
                    rowId={accountRowId(account.id)}
                    account={account}
                    onClick={() => {
                      navigate(`/${redirectType}/${account.id}`);
                    }}
                    onPerformanceCalculated={setPerformanceValues}
                  />
                  {referenceAccount && (
                    <AccountView
                      isLast={isLastAccountInGroup && !isLastGroup}
                      account={referenceAccount}
                      onClick={() => {
                        navigate(`/accounts/${referenceAccount.id}`);
                      }}
                      onPerformanceCalculated={setPerformanceValues}
                    />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <NoDataContainer>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                fontWeight={600}
                p={theme.spacing(6, 0)}
              >
                {t("depotsAccountsPage.noResults")}
              </Typography>
            </NoDataContainer>
          )}
        </>
      )}
    </Container>
  );
};
